import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  CheckCircle,
  Clock
} from 'lucide-react';
import { paymentAPI } from '../../services/api';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import Loader from '../common/Loader';

const Success = () => {
  const [searchParams] = useSearchParams();
  const [payment, setPayment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [countdown, setCountdown] = useState(5);
  const [showCountdown, setShowCountdown] = useState(false);
  const sessionId = searchParams.get('session_id');

  const fetchPaymentDetails = useCallback(async () => {
    try {
      const response = await paymentAPI.getPaymentRequestBySessionPublic(sessionId);
      if (response.success) {
        setPayment(response.data);
      } else {
        toast.error('Payment details not found');
      }
    } catch (error) {
      console.error('Error fetching payment details:', error);
      toast.error('Failed to load payment details');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      fetchPaymentDetails();
    } else {
      setIsLoading(false);
    }
  }, [sessionId, fetchPaymentDetails]);

  // Auto-close functionality
  useEffect(() => {
    if (!isLoading && payment) {
      setShowCountdown(true);
      
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Close the window/tab
            window.close();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isLoading, payment]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader size="lg" text="Loading payment details..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          {/* Success Icon */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>

          {/* Success Message */}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Payment Successful!
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Thank you for your payment. Your transaction has been completed successfully.
          </p>

          {/* Payment Details */}
          {payment && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h2>
              <div className="space-y-3 text-left">
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(payment.amount, payment.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Description:</span>
                  <span className="font-medium text-gray-900">{payment.description}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Date:</span>
                  <span className="font-medium text-gray-900">
                    {formatDateTime(payment.paidAt)}
                  </span>
                </div>
                {payment.stripePaymentIntentId && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transaction ID:</span>
                    <span className="font-medium text-gray-900 text-sm">
                      {payment.stripePaymentIntentId}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}



          {/* Countdown Timer */}
          {showCountdown && (
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-center space-x-2 mb-3">
                <Clock className="h-5 w-5 text-blue-600" />
                <p className="text-sm text-blue-800">
                  This window will close automatically in{' '}
                  <span className="font-semibold text-blue-900">{countdown}</span>{' '}
                  second{countdown !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="text-center">
                <button
                  onClick={() => window.close()}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  Close window manually
                </button>
              </div>
            </div>
          )}

          {/* Additional Information */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              If you have any questions about this payment, please contact us.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Success;
